import { useCallback, useEffect, useState } from "react";
import { fetchHotWallets, type ApiErrorInfo, type ApiHotWallet } from "@/lib/api";

interface UseHotWalletsResult {
  wallets: ApiHotWallet[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** List hook for HW-S1 (hot wallets overview). Mirrors use-wallets.ts: an
 * empty list is a real answer ("no hot wallets yet"), never backfilled with
 * placeholder rows. */
export function useHotWallets(): UseHotWalletsResult {
  const [wallets, setWallets] = useState<ApiHotWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHotWallets();
      if (result.error) {
        setWallets([]);
        setError(result.error);
      } else {
        setWallets(result.items);
      }
    } catch {
      setWallets([]);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { wallets, loading, error, refresh: load };
}
